import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, CircularProgress } from '@mui/material';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import { actionIcon } from '../ui/actionIcons';
import { ResourceFolder, ResourceLink } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useResources } from '../../hooks/useResources';
import { t } from '../../utils/translations';
import ConfirmDialog from '../common/ConfirmDialog';
import ResourcesTree from './ResourcesTree';
import ResourceFolderDialog from './ResourceFolderDialog';
import ResourceLinkDialog from './ResourceLinkDialog';
import ResourcesSearchToolbar from './ResourcesSearchToolbar';
import ResourceSearchResults from './ResourceSearchResults';
import {
  ResourceKindFilter,
  collectAncestorFolderIds,
  countResourceResultsByKind,
  isFolderVisibleInTree,
  searchResourceFolders,
  searchResourceLinks,
} from './resourcesUtils';
import {
  clearExpandedFolderIds,
  loadExpandedFolderIds,
  saveExpandedFolderIds,
} from './resourcesCache';

type FolderDialogState =
  | { open: false }
  | { open: true; mode: 'create' | 'edit'; folder?: ResourceFolder; defaultParentId?: string | null };

type LinkDialogState =
  | { open: false }
  | { open: true; mode: 'create' | 'edit'; link?: ResourceLink; defaultFolderId?: string | null };

type PendingDelete =
  | { type: 'folder'; folder: ResourceFolder }
  | { type: 'link'; link: ResourceLink }
  | null;

const ResourcesPage: React.FC = () => {
  const { currentUser } = useAuth();
  const data = useResources();
  const [folderDialog, setFolderDialog] = useState<FolderDialogState>({ open: false });
  const [linkDialog, setLinkDialog] = useState<LinkDialogState>({ open: false });
  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [kindFilter, setKindFilter] = useState<ResourceKindFilter>('all');
  const [revealFolderId, setRevealFolderId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(loadExpandedFolderIds())
  );

  // Persist open folders across reload; clear when leaving via in-app navigation.
  useEffect(() => {
    let leavingViaSpa = true;
    const onBeforeUnload = () => {
      leavingViaSpa = false;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (leavingViaSpa) {
        clearExpandedFolderIds();
      }
    };
  }, []);

  const handleToggleExpanded = useCallback((folderId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      saveExpandedFolderIds(Array.from(next));
      return next;
    });
  }, []);

  const handleEnsureExpanded = useCallback((folderId: string) => {
    setExpandedIds((prev) => {
      if (prev.has(folderId)) return prev;
      const next = new Set(prev);
      next.add(folderId);
      saveExpandedFolderIds(Array.from(next));
      return next;
    });
  }, []);

  const visibleFolders = useMemo(() => {
    const byId = new Map(data.folders.map((f) => [f.id, f]));
    return data.folders
      .filter((f) => isFolderVisibleInTree(currentUser?.role, f, byId))
      .sort((a, b) => {
        const aRoot = a.parentId ? 1 : 0;
        const bRoot = b.parentId ? 1 : 0;
        if (aRoot !== bRoot) return aRoot - bRoot;
        return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
      });
  }, [data.folders, currentUser?.role]);

  const isSearching = searchQuery.trim().length > 0;

  // Search runs over already-loaded data — no extra Firestore reads.
  const searchResults = useMemo(
    () => searchResourceLinks(currentUser?.role, data.folders, data.links, searchQuery),
    [currentUser?.role, data.folders, data.links, searchQuery]
  );

  const folderResults = useMemo(
    () => searchResourceFolders(currentUser?.role, data.folders, data.links, searchQuery),
    [currentUser?.role, data.folders, data.links, searchQuery]
  );

  const kindCounts = useMemo(
    () => countResourceResultsByKind(searchResults),
    [searchResults]
  );

  const filteredResults = useMemo(
    () =>
      kindFilter === 'all'
        ? searchResults
        : searchResults.filter((r) => r.link.kind === kindFilter),
    [searchResults, kindFilter]
  );

  // Drop a kind filter that no longer has any hit, so results never look empty
  // because of a stale chip.
  useEffect(() => {
    if (kindFilter !== 'all' && kindCounts[kindFilter] === 0) {
      setKindFilter('all');
    }
  }, [kindFilter, kindCounts]);

  /** Picking a folder result leaves search and reveals it inside the tree. */
  const handleOpenFolder = useCallback(
    (folder: ResourceFolder) => {
      setSearchQuery('');
      setKindFilter('all');
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.add(folder.id);
        for (const id of collectAncestorFolderIds(folder.id, data.folders)) {
          next.add(id);
        }
        saveExpandedFolderIds(Array.from(next));
        return next;
      });
      setRevealFolderId(folder.id);
    },
    [data.folders]
  );

  useEffect(() => {
    if (!revealFolderId || isSearching) return;
    document
      .getElementById(`resource-folder-${revealFolderId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setRevealFolderId(null);
  }, [revealFolderId, isSearching]);

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteLoading(true);
    try {
      if (pendingDelete.type === 'folder') {
        await data.deleteFolder(pendingDelete.folder.id);
      } else {
        await data.deleteLink(pendingDelete.link.id);
      }
      setPendingDelete(null);
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm font-medium text-amber-950 sm:max-w-xl">
          {t.resources.accessHint}
        </p>
        {data.canManage && (
          <Button
            variant="primary"
            icon={actionIcon.addFolder}
            className="shrink-0"
            onClick={() =>
              setFolderDialog({ open: true, mode: 'create', defaultParentId: null })
            }
          >
            {t.resources.addFolder}
          </Button>
        )}
      </div>

      {data.error && (
        <Alert severity="error" sx={{ mb: 3 }} className="rounded-xl">
          {data.error}
        </Alert>
      )}

      {data.loading && data.tree.length === 0 ? (
        <div className="flex justify-center py-16">
          <CircularProgress size={32} />
        </div>
      ) : data.tree.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center">
          <BookOpenIcon className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-700">{t.resources.empty}</p>
          <p className="mt-1 text-xs text-slate-500">{t.resources.emptyHint}</p>
          {data.canManage && (
            <Button
              variant="primary"
              icon={actionIcon.addFolder}
              className="mt-4"
              onClick={() =>
                setFolderDialog({ open: true, mode: 'create', defaultParentId: null })
              }
            >
              {t.resources.addFolder}
            </Button>
          )}
        </div>
      ) : (
        <>
          <ResourcesSearchToolbar
            query={searchQuery}
            onQueryChange={setSearchQuery}
            kindFilter={kindFilter}
            onKindFilterChange={setKindFilter}
            kindCounts={kindCounts}
            totalCount={searchResults.length}
            filteredCount={filteredResults.length}
            showResultsMeta={isSearching}
          />

          {isSearching ? (
            <ResourceSearchResults
              folderResults={folderResults}
              results={filteredResults}
              query={searchQuery}
              canManage={data.canManage}
              onOpenFolder={handleOpenFolder}
              onEditLink={(link) => setLinkDialog({ open: true, mode: 'edit', link })}
              onDeleteLink={(link) => setPendingDelete({ type: 'link', link })}
            />
          ) : (
            <ResourcesTree
              tree={data.tree}
              canManage={data.canManage}
              expandedIds={expandedIds}
              onToggleExpanded={handleToggleExpanded}
              onEnsureExpanded={handleEnsureExpanded}
              onAddSubfolder={(parent) =>
                setFolderDialog({ open: true, mode: 'create', defaultParentId: parent.id })
              }
              onAddLink={(folder) =>
                setLinkDialog({ open: true, mode: 'create', defaultFolderId: folder.id })
              }
              onEditFolder={(folder) =>
                setFolderDialog({ open: true, mode: 'edit', folder })
              }
              onDeleteFolder={(folder) => setPendingDelete({ type: 'folder', folder })}
              onEditLink={(link) => setLinkDialog({ open: true, mode: 'edit', link })}
              onDeleteLink={(link) => setPendingDelete({ type: 'link', link })}
              onMoveLink={async (linkId, targetFolderId) => {
                await data.moveLink(linkId, targetFolderId);
                setExpandedIds((prev) => {
                  if (prev.has(targetFolderId)) return prev;
                  const next = new Set(prev);
                  next.add(targetFolderId);
                  saveExpandedFolderIds(Array.from(next));
                  return next;
                });
              }}
            />
          )}
        </>
      )}

      <ResourceFolderDialog
        open={folderDialog.open}
        mode={folderDialog.open ? folderDialog.mode : 'create'}
        initial={folderDialog.open && folderDialog.mode === 'edit' ? folderDialog.folder : null}
        defaultParentId={
          folderDialog.open && folderDialog.mode === 'create'
            ? folderDialog.defaultParentId ?? null
            : null
        }
        parentLabel={(() => {
          if (!folderDialog.open) return null;
          if (folderDialog.mode === 'create' && folderDialog.defaultParentId) {
            return (
              data.folders.find((f) => f.id === folderDialog.defaultParentId)?.name ?? null
            );
          }
          return null;
        })()}
        allFolders={data.folders}
        saving={data.saving}
        onClose={() => setFolderDialog({ open: false })}
        onSubmit={async (input) => {
          if (folderDialog.open && folderDialog.mode === 'edit' && folderDialog.folder) {
            await data.updateFolder(folderDialog.folder.id, input);
          } else {
            await data.createFolder(input);
          }
        }}
      />

      <ResourceLinkDialog
        open={linkDialog.open}
        mode={linkDialog.open ? linkDialog.mode : 'create'}
        initial={linkDialog.open && linkDialog.mode === 'edit' ? linkDialog.link : null}
        defaultFolderId={
          linkDialog.open && linkDialog.mode === 'create'
            ? linkDialog.defaultFolderId ?? null
            : null
        }
        folders={visibleFolders}
        saving={data.saving}
        onClose={() => setLinkDialog({ open: false })}
        onSubmit={async (input) => {
          if (linkDialog.open && linkDialog.mode === 'edit' && linkDialog.link) {
            await data.updateLink(linkDialog.link.id, input);
          } else {
            await data.createLink(input);
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title={
          pendingDelete?.type === 'folder'
            ? t.resources.deleteFolderTitle
            : t.resources.deleteLinkTitle
        }
        message={
          pendingDelete?.type === 'folder'
            ? t.resources.deleteFolderConfirm.replace('{name}', pendingDelete.folder.name)
            : t.resources.deleteLinkConfirm.replace(
                '{name}',
                pendingDelete?.type === 'link' ? pendingDelete.link.title : ''
              )
        }
        warningMessage={
          pendingDelete?.type === 'folder' ? t.resources.deleteFolderWarning : undefined
        }
        loading={deleteLoading}
        onConfirm={handleConfirmDelete}
        onClose={() => {
          if (!deleteLoading) setPendingDelete(null);
        }}
      />
    </div>
  );
};

export default ResourcesPage;
